import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "min-h-screen bg-gray-100 dark:bg-gray-900" }
const _hoisted_2 = { class: "sticky top-0 z-30 flex h-16 flex-shrink-0 bg-white dark:bg-gray-800 shadow" }
const _hoisted_3 = { class: "flex flex-1 justify-between px-4" }
const _hoisted_4 = { class: "flex flex-1 items-center" }
const _hoisted_5 = { class: "sr-only" }
const _hoisted_6 = { class: "ml-auto flex items-center md:ml-6" }
const _hoisted_7 = { class: "relative ml-3" }
const _hoisted_8 = { class: "h-8 w-8 rounded-full overflow-hidden bg-secondary flex items-center justify-center text-white" }
const _hoisted_9 = ["src", "alt"]
const _hoisted_10 = { key: 1 }
const _hoisted_11 = {
  key: 0,
  class: "absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none",
  role: "menu"
}
const _hoisted_12 = { class: "px-4 py-2 text-sm text-gray-700 dark:text-gray-200" }
const _hoisted_13 = ["title"]
const _hoisted_14 = ["title"]
const _hoisted_15 = { class: "flex items-center" }
const _hoisted_16 = { class: "truncate" }
const _hoisted_17 = { class: "flex items-center" }
const _hoisted_18 = {
  key: 0,
  class: "ml-auto h-2 w-2 rounded-full bg-indigo-500",
  "aria-hidden": "true"
}
const _hoisted_19 = ["disabled"]
const _hoisted_20 = { class: "flex items-center" }
const _hoisted_21 = { class: "truncate" }
const _hoisted_22 = { class: "py-6" }
const _hoisted_23 = { class: "mx-auto max-w-8xl px-4 sm:px-6 lg:px-8" }

import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useThemeStore } from '@/stores/theme'
import { useClickOutside } from '@/composables/ui/useClickOutside'
import { useUser } from '@/composables/auth/useUser'
import { useLogin } from '@/composables/auth/useLogin'
import Sidebar from './Sidebar.vue'
import { Menu, User, LogOut, Sun, Moon, PanelLeftClose, PanelLeftOpen } from 'lucide-vue-next'

// Store Instances

export default /*@__PURE__*/_defineComponent({
  __name: 'AppLayout',
  setup(__props) {

// Imports
const themeStore = useThemeStore()
const route = useRoute()
const { userInitials, userName, userEmail, userImage } = useUser()
const { handleLogout, loading: isLoading } = useLogin()

// State
const isProfileOpen = ref(false)
const isSidebarOpen = ref(false)
const isSidebarCollapsed = ref(localStorage.getItem('sidebarCollapsed') === 'true')

// Computed Properties
const isProfileActive = computed(() => route.path === '/profile')
const isDarkMode = computed(() => themeStore.isDarkMode)

// Methods
const toggleProfile = () => {
  isProfileOpen.value = !isProfileOpen.value
}

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value
}

const toggleSidebarCollapse = () => {
  isSidebarCollapsed.value = !isSidebarCollapsed.value
  localStorage.setItem('sidebarCollapsed', isSidebarCollapsed.value.toString())
}

const toggleTheme = () => {
  themeStore.toggleTheme()
}

// Lifecycle Hooks
const { handleClickOutside } = useClickOutside(isProfileOpen)

// Watch for route changes to close sidebar on mobile
watch(
  () => route.path,
  () => {
    if (isSidebarOpen.value) {
      isSidebarOpen.value = false
    }
  }
)

onMounted(() => {
  themeStore.initializeTheme()
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(Sidebar, {
      isOpen: isSidebarOpen.value,
      "onUpdate:isOpen": _cache[0] || (_cache[0] = ($event: any) => ((isSidebarOpen).value = $event)),
      isCollapsed: isSidebarCollapsed.value,
      "onUpdate:isCollapsed": _cache[1] || (_cache[1] = ($event: any) => ((isSidebarCollapsed).value = $event))
    }, null, 8, ["isOpen", "isCollapsed"]),
    _createElementVNode("div", {
      class: _normalizeClass(["transition-all duration-300 ease-in-out", {
        'lg:pl-64': !isSidebarCollapsed.value,
        'lg:pl-20': isSidebarCollapsed.value
      }])
    }, [
      _createElementVNode("header", _hoisted_2, [
        _createElementVNode("button", {
          type: "button",
          class: "px-4 text-gray-500 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 lg:hidden",
          onClick: toggleSidebar
        }, [
          _cache[4] || (_cache[4] = _createElementVNode("span", { class: "sr-only" }, "Open sidebar", -1)),
          _createVNode(_unref(Menu), { class: "h-6 w-6" })
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("button", {
              type: "button",
              class: "hidden lg:flex items-center justify-center w-10 h-10 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500",
              onClick: toggleSidebarCollapse
            }, [
              _createElementVNode("span", _hoisted_5, _toDisplayString(isSidebarCollapsed.value ? 'Expand sidebar' : 'Collapse sidebar'), 1),
              (isSidebarCollapsed.value)
                ? (_openBlock(), _createBlock(_unref(PanelLeftOpen), {
                    key: 0,
                    class: "h-5 w-5"
                  }))
                : (_openBlock(), _createBlock(_unref(PanelLeftClose), {
                    key: 1,
                    class: "h-5 w-5"
                  }))
            ])
          ]),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("div", null, [
                _createElementVNode("button", {
                  type: "button",
                  class: "flex max-w-xs items-center rounded-full bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2",
                  onClick: toggleProfile
                }, [
                  _cache[5] || (_cache[5] = _createElementVNode("span", { class: "sr-only" }, "Open user menu", -1)),
                  _createElementVNode("div", _hoisted_8, [
                    (_unref(userImage))
                      ? (_openBlock(), _createElementBlock("img", {
                          key: 0,
                          src: _unref(userImage),
                          alt: _unref(userName),
                          class: "w-full h-full object-cover"
                        }, null, 8, _hoisted_9))
                      : (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(_unref(userInitials)), 1))
                  ])
                ])
              ]),
              (isProfileOpen.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                    _createElementVNode("div", _hoisted_12, [
                      _createElementVNode("p", {
                        class: "font-medium truncate",
                        title: _unref(userName)
                      }, _toDisplayString(_unref(userName)), 9, _hoisted_13),
                      _createElementVNode("p", {
                        class: "text-gray-500 dark:text-gray-400 truncate",
                        title: _unref(userEmail)
                      }, _toDisplayString(_unref(userEmail)), 9, _hoisted_14)
                    ]),
                    _cache[7] || (_cache[7] = _createElementVNode("div", { class: "border-t border-gray-100 dark:border-gray-700" }, null, -1)),
                    _createElementVNode("button", {
                      type: "button",
                      class: "block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700",
                      onClick: toggleTheme
                    }, [
                      _createElementVNode("div", _hoisted_15, [
                        (_openBlock(), _createBlock(_resolveDynamicComponent(isDarkMode.value ? _unref(Sun) : _unref(Moon)), { class: "mr-3 h-5 w-5 text-gray-400 flex-shrink-0" })),
                        _createElementVNode("span", _hoisted_16, _toDisplayString(isDarkMode.value ? 'Light Mode' : 'Dark Mode'), 1)
                      ])
                    ]),
                    _createVNode(_component_router_link, {
                      to: "/profile",
                      class: _normalizeClass(["block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700", { 'bg-gray-100 dark:bg-gray-700': isProfileActive.value }]),
                      onClick: _cache[2] || (_cache[2] = ($event: any) => (isProfileOpen.value = false))
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_17, [
                          _createVNode(_unref(User), { class: "mr-3 h-5 w-5 text-gray-400" }),
                          _cache[6] || (_cache[6] = _createElementVNode("span", null, "My Profile", -1)),
                          (isProfileActive.value)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_18))
                            : _createCommentVNode("", true)
                        ])
                      ]),
                      _: 1
                    }, 8, ["class"]),
                    _createElementVNode("button", {
                      type: "button",
                      class: "block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed",
                      onClick: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(handleLogout) && _unref(handleLogout)(...args))),
                      disabled: _unref(isLoading)
                    }, [
                      _createElementVNode("div", _hoisted_20, [
                        _createVNode(_unref(LogOut), { class: "mr-3 h-5 w-5 text-gray-400 flex-shrink-0" }),
                        _createElementVNode("span", _hoisted_21, _toDisplayString(_unref(isLoading) ? 'Logging out...' : 'Sign out'), 1)
                      ])
                    ], 8, _hoisted_19)
                  ]))
                : _createCommentVNode("", true)
            ])
          ])
        ])
      ]),
      _createElementVNode("main", _hoisted_22, [
        _createElementVNode("div", _hoisted_23, [
          _createVNode(_component_router_view)
        ])
      ])
    ], 2)
  ]))
}
}

})